import { supabase } from './supabase';
import { ProgressCallback } from './progressTracker';
import { UploadManager } from './uploadManager';
import { RetryUtils, RetryableError } from './retryUtils';

export class StorageService {
  static readonly ENCRYPTION_CHUNK_SIZE = 5 * 1024 * 1024;
  static readonly MAX_SIMPLE_UPLOAD_SIZE = 50 * 1024 * 1024;

  private static arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 32768;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return btoa(binary);
  }

  static async uploadFile(
    fileBlob: Blob,
    fileName: string,
    contentType: string,
    onProgress?: ProgressCallback
  ): Promise<string | undefined> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const fileSize = fileBlob.size;

      if (fileSize > this.MAX_SIMPLE_UPLOAD_SIZE) {
        console.log('[StorageService] File too large for simple upload, using chunked upload');

        const file = new File([fileBlob], fileName, { type: contentType });

        const category = this.determineFileCategory(contentType);

        const result = await UploadManager.startUpload(file, {
          category,
          encrypt: false,
          onProgress,
        });

        return result.url;
      }

      const fullFileName = `${user.user.id}/${fileName}`;

      if (onProgress) onProgress(5);

      const arrayBuffer = await fileBlob.arrayBuffer();

      if (onProgress) onProgress(20);

      console.log('[StorageService] Uploading file:', fullFileName, 'Size:', arrayBuffer.byteLength);

      const base64Data = this.arrayBufferToBase64(arrayBuffer);

      if (onProgress) onProgress(40);

      const url = await this.uploadToBackblazeWithRetry(
        fullFileName,
        base64Data,
        contentType,
        onProgress
      );

      if (onProgress) onProgress(100);

      console.log('[StorageService] Upload successful:', url);
      return url;
    } catch (error) {
      console.error('[StorageService] Upload error:', error);
      return undefined;
    }
  }

  private static async uploadToBackblazeWithRetry(
    fileName: string,
    base64Data: string,
    contentType: string,
    onProgress?: ProgressCallback
  ): Promise<string> {
    return RetryUtils.withRetry(
      async () => {
        const uploadUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/upload-to-backblaze`;

        const uploadResponse = await fetch(uploadUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fileName,
            fileData: base64Data,
            contentType,
            isBase64: true,
          }),
        });

        if (onProgress) onProgress(80);

        if (!uploadResponse.ok) {
          const errorText = await uploadResponse.text();
          console.error('[StorageService] Upload failed:', uploadResponse.status, errorText);

          if (uploadResponse.status === 401 || uploadResponse.status === 403) {
            throw new Error('Authentication failed');
          }

          throw new RetryableError(`Upload failed: ${uploadResponse.status} - ${errorText}`);
        }

        const uploadResult = await uploadResponse.json();

        if (!uploadResult.url) {
          console.error('[StorageService] No URL in upload response');
          throw new Error('No URL in upload response');
        }

        return uploadResult.url;
      },
      {
        maxAttempts: 3,
        onRetry: (attempt, error) => {
          console.log(`[StorageService] Retry attempt ${attempt}:`, error.message);
        },
      }
    );
  }

  private static determineFileCategory(contentType: string): 'image' | 'video' | 'encrypted' {
    if (contentType.startsWith('image/')) {
      return 'image';
    }
    if (contentType.startsWith('video/')) {
      return 'video';
    }
    return 'encrypted';
  }

  static generateFileName(extension: string): string {
    return `${Date.now()}-${Math.random().toString(36).substring(7)}.${extension}`;
  }

  static async uploadEncryptedChunks(
    chunks: { encrypted: ArrayBuffer; iv: string; hash: string; size: number }[],
    fileName: string,
    onProgress?: ProgressCallback
  ): Promise<string | undefined> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const fullFileName = `${user.user.id}/${fileName}`;

      if (onProgress) onProgress(5);

      console.log(`[StorageService] Uploading ${chunks.length} encrypted chunks for file: ${fullFileName}`);

      const combinedBuffer = new Uint8Array(
        chunks.reduce((sum, chunk) => sum + chunk.encrypted.byteLength, 0)
      );

      let offset = 0;
      for (const chunk of chunks) {
        combinedBuffer.set(new Uint8Array(chunk.encrypted), offset);
        offset += chunk.encrypted.byteLength;
      }

      if (onProgress) onProgress(20);

      const base64Data = this.arrayBufferToBase64(combinedBuffer.buffer);

      if (onProgress) onProgress(40);

      const url = await this.uploadToBackblazeWithRetry(
        fullFileName,
        base64Data,
        'application/octet-stream',
        onProgress
      );

      for (let i = 0; i < chunks.length; i++) {
        await supabase.from('file_chunks').insert({
          file_path: fullFileName,
          chunk_index: i,
          iv: chunks[i].iv,
          sha256_hash: chunks[i].hash,
          encrypted_size: chunks[i].size,
          user_id: user.user.id,
        });
      }

      if (onProgress) onProgress(100);

      console.log('[StorageService] Upload successful:', url);
      return url;
    } catch (error) {
      console.error('[StorageService] Upload error:', error);
      return undefined;
    }
  }

  static async deleteFile(fileUrl: string): Promise<boolean> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      const urlParts = fileUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const fullFileName = `${user.user.id}/${decodeURIComponent(fileName)}`;

      await RetryUtils.withRetry(
        async () => {
          const edgeFunctionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/delete-from-backblaze`;

          const response = await fetch(edgeFunctionUrl, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              fileName: fullFileName,
            }),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new RetryableError(`Delete failed: ${response.status} - ${errorText}`);
          }
        },
        {
          maxAttempts: 2,
          onRetry: (attempt, error) => {
            console.log(`[StorageService] Delete retry attempt ${attempt}:`, error.message);
          },
        }
      );

      return true;
    } catch (error) {
      console.error('[StorageService] Delete error:', error);
      return false;
    }
  }
}
